import { Injectable } from '@angular/core';

/**
 * A single failed HTTP request captured for bug-report context.
 * `traceId` is the value of the X-Trace-Id response header (set by core), used
 * later to correlate the report to the matching server log / Sentry event.
 */
export interface CapturedFailedRequest {
    method: string;
    url: string;
    status: number;
    traceId: string | null;
    timestamp: string;
}

/**
 * Always-on, in-memory rolling buffer of the most recent failed HTTP requests.
 * Populated by CaptureHttpInterceptor; read at bug-report submit time. Bounded so
 * a long session can't grow it without limit — only the last MAX_ENTRIES are kept.
 */
@Injectable({ providedIn: 'root' })
export class FailedRequestBufferService {
    public static readonly MAX_ENTRIES = 10;

    private _buffer: CapturedFailedRequest[] = [];

    /**
     * The most recent non-empty X-Trace-Id seen on ANY response (success or
     * failure). Lets a bug report surface a real trace id even when nothing
     * failed in this session.
     */
    private _lastTraceId: string | null = null;

    /** Record a failed request, evicting the oldest entry once full (ring buffer). */
    public record(entry: CapturedFailedRequest): void {
        this._buffer.push(entry);
        if (this._buffer.length > FailedRequestBufferService.MAX_ENTRIES) {
            this._buffer.shift();
        }
    }

    /** Remember the most-recent non-empty trace id observed on a response. */
    public recordTraceId(traceId: string | null): void {
        if (traceId && traceId.length > 0) {
            this._lastTraceId = traceId;
        }
    }

    /** The most recent trace id seen on any response, or null if none. */
    public getLastTraceId(): string | null {
        return this._lastTraceId;
    }

    /** Return a defensive copy so callers cannot mutate the internal buffer. */
    public getAll(): CapturedFailedRequest[] {
        return [...this._buffer];
    }

    /** Clear the buffer — called after a bug report is successfully submitted. */
    public clear(): void {
        this._buffer = [];
        this._lastTraceId = null;
    }
}
